import { usePagination } from '@mantine/hooks';
import { DataTableSortStatus } from 'mantine-datatable';
import { useClientPaginatedTable } from './useClientPaginatedTable';
import { useServerPaginatedTable } from './useServerPaginatedTable';
import { BaseRecord, SortableColumn } from '../types';

export type TableMode = 'client' | 'server';
export type PaginationData = ReturnType<typeof usePagination>;

type UseTableOptions<T> = {
  resource: string;
  mode?: TableMode;
  columns?: SortableColumn<T>[];
  searchTerm?: string;
  filterPredicate?: (item: T, search: string) => boolean;
  paginationDependencies?: unknown;
  filters?: Record<string, unknown>;
  pageSize?: number;
  useLocalPagination?: boolean;
  initialSort?: DataTableSortStatus<T>;
};

export type TableData<T> = {
  data: T[];
  isLoading: boolean;
  isFetching: boolean;
  isError: boolean;
  error: unknown;
  pagination: PaginationData;
  totalRecords: number;
  refetch: () => void;
  sortStatus?: DataTableSortStatus<T>;
  onSortStatusChange?: (status: DataTableSortStatus<T>) => void;
};

export const useTable = <T extends BaseRecord>(options: UseTableOptions<T>): TableData<T> => {
  const mode = options.mode || 'client';
  const pageSize = options.pageSize || 10;

  const serverTable = useServerPaginatedTable<T>(
    options.resource,
    pageSize,
    options.filters,
    options.initialSort,
    mode === 'server',
  );
  const clientTable = useClientPaginatedTable<T>({
    ...options,
    columns: options.columns || [],
    searchTerm: options.searchTerm || '',
    pageSize,
    useLocalPagination: options.useLocalPagination || false,
    enabled: mode === 'client',
  });

  return mode === 'server' ? serverTable : clientTable;
};
